import {
  DashboardDefinition,
  isDashboardDefinition,
} from "../DashboardHeader/DashboardParams";

/**
 * localStorage persistence for the metrics dashboard's UNSAVED view state.
 *
 * The URL carries only the dashboard's top-level configuration (range,
 * period, active saved-dashboard id); the layout itself lives in component
 * state. So that in-progress edits survive a reload or navigation, every
 * LAYOUT edit snapshots the full {@link DashboardDefinition} here as a
 * "draft", keyed by context (org + device/session scope + saved-dashboard
 * id) so drafts never bleed across orgs, scopes, or dashboards. A
 * range/period tweak alone never creates a draft (those params live in the
 * URL, and a params-only draft would be dropped un-prompted on the next
 * visit — see `planDraftRestore`); it only refreshes an existing one.
 *
 * A draft is cleared when it stops being an unsaved edit: after a
 * successful Save / Save-as (the record now matches), or when the user
 * discards by switching to "New unsaved view".
 *
 * Drafts also carry a sliding TTL ({@link DRAFT_TTL_MS}): each value is
 * stored as an envelope stamped with its last write time, and every write
 * (edit, params refresh, restore) resets the clock. A draft untouched for
 * longer than the TTL is dropped on read ({@link loadDraft}) and by the
 * mount-time {@link sweepExpiredDrafts}, so abandoned drafts don't pile up
 * in localStorage forever. Pre-TTL entries (bare definitions with no
 * envelope) are never treated as expired — they're valid unsaved user work
 * — and are migrated to a now-stamped envelope on first touch.
 */

const DRAFT_KEY_PREFIX = "roboto.metricsDashboard.draft";

/** How long an untouched draft survives before it's dropped. Sliding:
 *  every {@link saveDraft} resets the clock. */
export const DRAFT_TTL_MS = 15 * 24 * 60 * 60 * 1000;

/**
 * The on-disk shape of one stored draft. Internal to this module — callers
 * only ever see the {@link DashboardDefinition} inside.
 */
interface StoredDraft {
  /** Epoch ms of the last write; the TTL clock's start. */
  savedAt: number;
  definition: DashboardDefinition;
}

/** Loose structural check for the envelope shape (any `definition` prop);
 *  field validity is judged separately so corrupt stamps can be healed. */
function isEnvelope(
  value: unknown,
): value is { savedAt?: unknown; definition: unknown } {
  return typeof value === "object" && value !== null && "definition" in value;
}

/** The identity of one dashboard view, for keying its draft. */
export interface DraftContext {
  /** Org the dashboard renders for. */
  orgId: string | undefined;
  /** Set when the dashboard is the device-detail Metrics tab. */
  deviceId?: string;
  /** Set when the dashboard is session-scoped. */
  sessionId?: string;
  /** The active saved-dashboard id, or null for the unsaved view. */
  dashboardId: string | null;
}

/** Build the localStorage key for one dashboard context's draft. */
export function draftKeyFor(context: DraftContext): string {
  let scope = "org";
  if (context.deviceId) {
    scope = `device:${context.deviceId}`;
  } else if (context.sessionId) {
    scope = `session:${context.sessionId}`;
  }
  return [
    DRAFT_KEY_PREFIX,
    context.orgId ?? "unknown-org",
    scope,
    context.dashboardId ?? "unsaved",
  ].join(":");
}

/**
 * Load the draft stored under `key`, or null when there is none. A draft
 * that no longer parses or is not a strictly valid current-schema
 * definition ({@link isDashboardDefinition}) is dropped (removed) rather
 * than surfaced — a corrupt or stale draft must never wedge the dashboard.
 * A valid draft last written more than {@link DRAFT_TTL_MS} ago is likewise
 * dropped as abandoned.
 *
 * Rollback caveat: older builds read the bare-definition format and will
 * purge envelopes as invalid — benign and self-healing (the draft is
 * best-effort state). Clock skew is deliberately ignored: a backwards
 * clock only makes a draft live longer, the safe failure mode.
 */
export function loadDraft(key: string): DashboardDefinition | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(key);
  } catch {
    return null; // Storage unavailable (privacy mode); behave as draft-less.
  }
  if (raw === null) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isEnvelope(parsed)) {
      if (isDashboardDefinition(parsed.definition)) {
        if (typeof parsed.savedAt === "number") {
          if (Date.now() - parsed.savedAt > DRAFT_TTL_MS) {
            clearDraft(key); // Abandoned — expired past the TTL.
            return null;
          }
          return parsed.definition;
        }
        // Corrupt stamp around a valid definition: heal by re-stamping
        // fresh-from-now rather than discarding real user work.
        saveDraft(key, parsed.definition);
        return parsed.definition;
      }
    } else if (isDashboardDefinition(parsed)) {
      // Legacy bare definition (pre-TTL format): migrate in place to a
      // now-stamped envelope. Never treated as expired — its age is
      // unknown and it's valid unsaved user work.
      saveDraft(key, parsed);
      return parsed;
    }
  } catch {
    // Fall through to removal below.
  }
  clearDraft(key);
  return null;
}

/** Persist `definition` as the draft for `key`, stamped now (resetting the
 *  sliding TTL clock). Failures (quota, privacy mode) are swallowed —
 *  persistence is best-effort, never load-bearing. */
export function saveDraft(key: string, definition: DashboardDefinition): void {
  const envelope: StoredDraft = { savedAt: Date.now(), definition };
  try {
    localStorage.setItem(key, JSON.stringify(envelope));
  } catch {
    // Best-effort only.
  }
}

/** Remove the draft stored under `key`, if any. */
export function clearDraft(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Best-effort only.
  }
}

/** The drafts present in one scope (org page, or one device tab). */
export interface ScopeDraftSummary {
  /** An unsaved-view ("New dashboard") draft exists in this scope. */
  unsaved: boolean;
  /** Saved-dashboard ids with a stored, valid draft in this scope. */
  savedDashboardIds: string[];
}

/**
 * Discover every draft stored for one scope (the org page, or one device's
 * Metrics tab) by scanning the draft key prefix. Only drafts that load as
 * valid current-schema definitions are reported; invalid ones are purged as a
 * side effect (via {@link loadDraft}). Landing-time selection treats a draft
 * as the remembered dashboard choice, so this is its discovery primitive.
 *
 * The scan is over `localStorage` keys with the exact
 * `<prefix>:<orgId>:<scope>:` prefix; the remainder of the key is the
 * saved-dashboard id, or the literal `unsaved` for the unsaved view
 * (dashboard ids never contain `:`).
 */
export function findScopeDrafts(
  context: Omit<DraftContext, "dashboardId">,
): ScopeDraftSummary {
  const scopePrefix = draftKeyFor({ ...context, dashboardId: null }).replace(
    /unsaved$/,
    "",
  );
  const summary: ScopeDraftSummary = { unsaved: false, savedDashboardIds: [] };
  let keys: string[] = [];
  try {
    keys = Object.keys(localStorage);
  } catch {
    return summary; // Storage unavailable; behave as draft-less.
  }
  for (const key of keys) {
    if (!key.startsWith(scopePrefix)) {
      continue;
    }
    if (loadDraft(key) === null) {
      continue; // Invalid draft — loadDraft already purged it.
    }
    const remainder = key.slice(scopePrefix.length);
    if (remainder === "unsaved") {
      summary.unsaved = true;
    } else if (remainder !== "") {
      summary.savedDashboardIds.push(remainder);
    }
  }
  // Deterministic order for callers that iterate/pick.
  summary.savedDashboardIds.sort();
  return summary;
}

/**
 * Drop every expired draft in localStorage — ALL orgs and scopes, unlike
 * the scope-filtered {@link findScopeDrafts} — so drafts for contexts the
 * user never revisits still age out. Legacy bare-definition entries are
 * migrated to a now-stamped envelope (starting their TTL clock). Fresh
 * drafts, non-draft keys, and unparseable/corrupt values are left alone —
 * purging invalid entries stays the lazy {@link loadDraft} path's job.
 * Best-effort: never throws.
 */
export function sweepExpiredDrafts(): void {
  let keys: string[] = [];
  try {
    keys = Object.keys(localStorage);
  } catch {
    return; // Storage unavailable; nothing to sweep.
  }
  for (const key of keys) {
    if (!key.startsWith(`${DRAFT_KEY_PREFIX}:`)) {
      continue;
    }
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) {
        continue;
      }
      const parsed: unknown = JSON.parse(raw);
      if (isEnvelope(parsed)) {
        if (
          typeof parsed.savedAt === "number" &&
          Date.now() - parsed.savedAt > DRAFT_TTL_MS
        ) {
          localStorage.removeItem(key);
        }
      } else if (isDashboardDefinition(parsed)) {
        // Legacy bare definition: stamp it now so its TTL clock starts.
        saveDraft(key, parsed);
      }
    } catch {
      // Unparseable value or storage hiccup — leave it; never throw.
    }
  }
}
